import { IBusinessUpshifter, IBusinessUpshiftersReducer } from 'models/BusinessUpshifter'
import { Action } from 'redux'
import { isType } from 'ts-action'
import { UnfavoriteUpshifter, FavoriteUpshifter, DeleteFavorite } from 'data/Favorite/actions'
import { deleteFromArray } from 'helpers/helperFunctions'
import {
  GetBusinessUpshifters,
  BusinessUpshiftersFetching,
  SetDeleteFromRosterLoading,
  DeleteFromRoster,
  AddToMultipleRosters,
  SetBusinessUpshiftersSorting,
  ResetBusinessUpshiftersSorting,
  MuteUnmuteBusinessForUser,
  SetMuteUnmuteBusinessLoading,
} from './actions'

const initialState: IBusinessUpshiftersReducer = {
  meta: {
    code: 0,
    message: '',
  },
  isFetching: false,
  data: [],
  sortingState: {
    columnId: null,
    direction: null,
  },
  deleteFromRosterLoadingIds: {},
  muteUnmuteBusinessLoadingIds: {},
}

export const businessUpshiftersReducer = (
  state = initialState,
  action: Action
): IBusinessUpshiftersReducer => {
  if (isType(action, GetBusinessUpshifters)) {
    const { payload } = action
    return {
      ...state,
      meta: payload.meta,
      data: payload.data,
    }
  }
  if (isType(action, BusinessUpshiftersFetching)) {
    const { payload } = action
    return {
      ...state,
      isFetching: payload,
    }
  }
  if (isType(action, SetBusinessUpshiftersSorting)) {
    const { payload } = action
    return {
      ...state,
      sortingState: payload,
    }
  }
  if (isType(action, ResetBusinessUpshiftersSorting)) {
    return {
      ...state,
      sortingState: {
        columnId: null,
        direction: null,
      },
    }
  }
  if (isType(action, UnfavoriteUpshifter)) {
    const { payload } = action
    const { data, meta } = state
    const formattedArray = data.filter((upshifter) => upshifter.favored_by_business?.id !== payload)
    return {
      ...state,
      data: formattedArray,
      meta: {
        ...meta,
        pagination: {
          ...meta.pagination,
          count: meta.pagination ? meta.pagination.count - 1 : 0,
          total: meta.pagination ? meta.pagination.total - 1 : 0,
          per_page: meta.pagination?.per_page || 50,
          current_page: meta.pagination?.current_page || 1,
          total_pages: meta.pagination?.total_pages || 1,
          links: meta.pagination?.links || [],
        },
      },
    }
  }
  if (isType(action, FavoriteUpshifter)) {
    const {
      payload: { data },
    } = action
    const updatedUpshifters = state.data.map((upshifter) => {
      if (upshifter.id === data.model_favored.id) {
        return {
          ...upshifter,
          favored_by_business: data,
        }
      }
      return upshifter
    })

    // If the Business user Favorites an Upshifter via All/Blocked tabs, then the Upshifter remains in the UpshiftersList list and here we just return the same list but with changed 'favorite' property.
    if (state.data.find((item) => item.id === data.model_favored.id)) {
      return {
        ...state,
        data: updatedUpshifters,
      }
    }
    // Otherwise, if the Business user Favorites an Upshifter via Favorites (My Team) tab, then the Upshifter needs to be added in the Upshifters List, since the list displayed in My Team tab is changing its list items depending on the Favorite/Unfavorite actions.
    return {
      ...state,
      data: [...updatedUpshifters, data.model_favored as unknown as IBusinessUpshifter],
    }
  }
  if (isType(action, DeleteFavorite)) {
    const { payload } = action
    const updatedUpshifters = state.data.map((upshifter) => {
      if (upshifter.id === payload.upshifterId) {
        return {
          ...upshifter,
          favored_by_business: null,
        }
      }
      return upshifter
    })

    return {
      ...state,
      data: updatedUpshifters,
    }
  }
  if (isType(action, SetDeleteFromRosterLoading)) {
    const { payload } = action
    return {
      ...state,
      deleteFromRosterLoadingIds: {
        ...state.deleteFromRosterLoadingIds,
        [payload.id]: payload.isLoading,
      },
    }
  }

  if (isType(action, DeleteFromRoster)) {
    const { payload } = action
    const { data } = state
    const formattedArray = deleteFromArray<IBusinessUpshifter>(data, payload)
    return {
      ...state,
      data: formattedArray,
      meta: {
        ...state.meta,
        pagination: {
          count: state.meta.pagination?.count ? state.meta.pagination.count - 1 : 0,
          total: state.meta.pagination?.total ? state.meta.pagination.total - 1 : 0,
          per_page: state.meta.pagination?.per_page ?? 50,
          current_page: state.meta.pagination?.current_page ?? 1,
          total_pages: state.meta.pagination?.total_pages ?? 1,
          links: state.meta.pagination?.links ?? [],
        },
      },
    }
  }

  if (isType(action, AddToMultipleRosters)) {
    const {
      payload: { upshifterId, rosters },
    } = action
    const { data } = state

    const updatedUpshifters = data.map((upshifter) => {
      if (upshifter.id === upshifterId) {
        return {
          ...upshifter,
          rosters: {
            ...upshifter.rosters,
            data: [...(upshifter.rosters?.data || []), ...rosters],
          },
        }
      }
      return upshifter
    })

    return {
      ...state,
      data: updatedUpshifters,
    }
  }

  if (isType(action, SetBusinessUpshiftersSorting)) {
    const { payload } = action
    return {
      ...state,
      sortingState: payload,
    }
  }
  if (isType(action, ResetBusinessUpshiftersSorting)) {
    return {
      ...state,
      sortingState: {
        columnId: null,
        direction: null,
      },
    }
  }

  if (isType(action, SetMuteUnmuteBusinessLoading)) {
    const { payload } = action
    return {
      ...state,
      muteUnmuteBusinessLoadingIds: {
        ...state.muteUnmuteBusinessLoadingIds,
        [payload.id]: payload.isLoading,
      },
    }
  }

  if (isType(action, MuteUnmuteBusinessForUser)) {
    const { payload } = action
    return {
      ...state,
      data: state.data.map((upshifter) => {
        if (upshifter.id === payload.upshifterId) {
          return {
            ...upshifter,
            muted_for_business: payload.mutedForBusiness,
          }
        }
        return upshifter
      }),
    }
  }

  return state
}
